import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react';
import {
  FormFieldFileUpload,
  FormFieldTablesInput
} from '@publishing-engine/domain/types/article';
import {
  ButtonIcon,
  ButtonText,
  Checkbox,
  FormFeedback,
  Input,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGripDotsVertical,
  faInfoCircle,
  faPlus,
  faTrashAlt
} from '@fortawesome/pro-regular-svg-icons';
import { ITablesValue } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Tables.js';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableRow from './SortableRow';
import { getValidationTypedMessage } from './common/errorMessages';
import FormFieldFigureSingleFileUpload from './FormFieldFigureSingleFileUpload';
import { formatMeta } from './FormFieldFigures';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { DeleteFigureModal } from 'src/components/Submission/GenericFieldComponents/Figures/DeleteFigureModal';

interface FormFieldTablesProps {
  config: FormFieldTablesInput;
  submissionId: string;
  defaultItem?: any;
  fields: any[];
  append: Function;
  remove: (index: number) => void;
  update: Function;
  selected: Record<string, boolean>;
  toggleRow: (id: string) => void;
  toggleSelectAll: () => void;
  methods: UseFormReturn<ITablesValue>;
  move: (oldIndex: number, newIndex: number) => void; // passed from parent useFieldArray
  onLastTableDeleted?: () => void;
}

export interface SubFigureItem {
  id: string;
  caption: string;
  file: any | null;
  metadata?: any | null;
}

export type ITableData = {
  caption: string;
  file: ISubmissionFile;
  id: string;
};

const FormFieldTables: React.FC<FormFieldTablesProps> = ({
  config,
  submissionId,
  defaultItem = { caption: '', file: null, metadata: null },
  fields,
  append,
  remove,
  selected,
  toggleRow,
  toggleSelectAll,
  methods,
  move,
  onLastTableDeleted
}) => {
  const {
    register,
    getValues,
    watch,
    formState: { errors },
    setValue
  } = methods;

  const toast = useToast();

  const [deleteTableModal, setDeleteTableModal] = useState(false);
  const [selectedTableIndex, setSelectedTableIndex] = useState(0);

  const {
    params: { validations },
    caseDataFields: { main: field }
  } = config;

  const parentSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    if (fields && fields.length === 0) {
      append(defaultItem);
    }
  }, [fields]);

  const onParentDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    if (oldIndex !== newIndex) {
      move(oldIndex, newIndex);
    }
  };

  const handleDeleteSingleTable = (index: number) => {
    const isLastTable = fields.length <= 1;
    remove(index);
    setDeleteTableModal(false);
    toast({
      variant: 'success',
      title: 'Table deleted successfully.'
    });
    if (isLastTable) {
      onLastTableDeleted?.();
    }
  };

  const maxItems = validations?.maxItems ?? Number.POSITIVE_INFINITY;

  const watchedFields = watch('fileSet.Tables');

  const totalItemsWithFile = useMemo(() => {
    if (!Array.isArray(watchedFields)) return 0;

    return watchedFields.length;
  }, [watchedFields]);

  const disableAddMore = totalItemsWithFile >= maxItems;

  return (
    <Flex flexDirection="column" gap={2}>
      <DndContext
        sensors={parentSensors}
        collisionDetection={closestCenter}
        onDragEnd={onParentDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <Table
          width="100%"
          sx={{
            '& th, & td': {
              verticalAlign: 'top'
            }
          }}
        >
          <Thead>
            <Tr
              sx={{
                '& th': {
                  textTransform: 'none',
                  color: 'black'
                }
              }}
            >
              <Th width="1%"></Th>
              <Th width="2%" ps={0}>
                <Checkbox
                  ariaLabel="Select All"
                  id="check-all"
                  name="check-all"
                  isChecked={
                    fields.length > 0 && fields.every(f => selected[f.id])
                  }
                  isIndeterminate={
                    fields.some(f => selected[f.id]) &&
                    !fields.every(f => selected[f.id])
                  }
                  onChange={toggleSelectAll}
                >
                  {''}
                </Checkbox>
              </Th>
              <Th ps={0} width="1%">
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Table No.
                </Text>
              </Th>
              <Th width="62%" ps={0}>
                <Text textStyle="headingItem">
                  Caption
                  <Box as="span" color="red.500">
                    *
                  </Box>
                </Text>
              </Th>
              <Th width="32%" ps={0}>
                <Text textStyle="headingItem">
                  File
                  <Box as="span" color="red.500">
                    *
                  </Box>
                </Text>
              </Th>
            </Tr>
          </Thead>

          <SortableContext
            items={fields.map(f => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <Tbody>
              {fields.map((item, index) => {
                const captionName = `${field}[${index}].caption`;
                const fileName = `${field}[${index}].file`;
                const metaDataField = `${field}[${index}].metadata`;
                const metaData = getValues(metaDataField as any);
                const maxLength =
                  validations?.items?.properties?.caption?.maxLength ?? 1000;

                const captionError =
                  errors?.fileSet?.Tables &&
                  Array.isArray(errors.fileSet.Tables) &&
                  errors.fileSet.Tables[index]?.caption;

                return (
                  <React.Fragment key={item.id}>
                    <SortableRow id={item.id} key={item.id}>
                      {({ attributes, listeners }: any) => (
                        <>
                          <Td {...attributes} {...listeners} cursor="grab">
                            <FontAwesomeIcon icon={faGripDotsVertical} />
                          </Td>

                          <Td ps={0}>
                            <Checkbox
                              ariaLabel={`Select table ${index + 1}`}
                              id={`select-table-${index}`}
                              name={`select-table-${index}`}
                              isChecked={selected[item.id] || false}
                              onChange={() => toggleRow(item.id)}
                            >
                              {''}
                            </Checkbox>
                          </Td>

                          <Td ps={0}>
                            <Text>Table {index + 1}</Text>
                          </Td>

                          <Td ps={0}>
                            <Input
                              placeholder="Enter caption"
                              {...register(captionName as any)}
                              onBlur={e => {
                                setValue(
                                  captionName as any,
                                  e.target.value.trim(),
                                  { shouldValidate: true }
                                );
                              }}
                              maxLength={maxLength}
                            />

                            {captionError && (
                              <Flex mt={2}>
                                <FormFeedback variant="error">
                                  {getValidationTypedMessage(
                                    captionName,
                                    validations.kind,
                                    validations,
                                    captionError
                                  )}
                                </FormFeedback>{' '}
                              </Flex>
                            )}
                          </Td>
                          <Td ps={0}>
                            <Flex
                              direction="column"
                              gap={3}
                              align={'self-start'}
                            >
                              <Flex alignItems="center" w="100%" gap={2}>
                                <Box flex="1">
                                  <FormFieldFigureSingleFileUpload
                                    submissionId={submissionId}
                                    submissionContextId={submissionId}
                                    methods={methods}
                                    config={
                                      {
                                        name: 'file',
                                        params: {
                                          validations: {
                                            required: true,
                                            maxSizeMB: validations.maxSizeMB,
                                            allowedFileFormats:
                                              validations.allowedFileFormats
                                          }
                                        },
                                        caseDataFields: { main: fileName }
                                      } as FormFieldFileUpload
                                    }
                                    FiledName="table"
                                  />
                                </Box>
                                <Flex width="88px" justify="flex-end" gap={2}>
                                  <Box
                                    w="32px"
                                    display="flex"
                                    justifyContent="center"
                                  >
                                    {metaData && (
                                      <Tooltip
                                        hasArrow
                                        placement="top"
                                        borderRadius={4}
                                        maxW="300px"
                                        p={3}
                                        bg="gray.50"
                                        color="black"
                                        fontSize="sm"
                                        whiteSpace="normal"
                                        data-cy="contributions-info-tooltip"
                                        label={
                                          <div>
                                            <strong
                                              style={{
                                                fontSize: '13px',
                                                display: 'block',
                                                marginBottom: '6px'
                                              }}
                                            >
                                              File Info
                                            </strong>

                                            {Object.entries(
                                              formatMeta(metaData)
                                            ).map(([key, value]) => (
                                              <div
                                                key={key}
                                                style={{
                                                  marginBottom: '3px'
                                                }}
                                              >
                                                <span
                                                  style={{ fontWeight: 600 }}
                                                >
                                                  {key}:{' '}
                                                </span>
                                                {value}
                                              </div>
                                            ))}
                                          </div>
                                        }
                                      >
                                        <span>
                                          <ButtonIcon
                                            size="regular"
                                            ariaLabel="figures-meta-info"
                                            icon={
                                              <FontAwesomeIcon
                                                icon={faInfoCircle}
                                              />
                                            }
                                            data-cy="contributions-info-icon"
                                            ml={1}
                                            tabIndex={-1}
                                          />
                                        </span>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Box w="32px">
                                    <ButtonIcon
                                      size="regular"
                                      ariaLabel={`Delete table ${index + 1}`}
                                      icon={
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                      }
                                      data-cy="delete-table-icon"
                                      onClick={() => {
                                        setSelectedTableIndex(index);
                                        setDeleteTableModal(true);
                                      }}
                                    />
                                  </Box>
                                </Flex>
                              </Flex>
                            </Flex>
                          </Td>
                        </>
                      )}
                    </SortableRow>
                  </React.Fragment>
                );
              })}
            </Tbody>
          </SortableContext>
        </Table>
      </DndContext>

      {/* Add row button */}
      <Flex justifyContent="flex-end" mt={2}>
        <Tooltip
          label={`You have reached the maximum of ${maxItems} tables. Please delete an existing table or move excess table to the supplementary material.`}
          isDisabled={!disableAddMore}
          hasArrow
          placement="top"
        >
          <span>
            <ButtonText
              size="regular"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              variant="secondary"
              onClick={() => append(defaultItem)}
              disabled={disableAddMore}
              _disabled={{ cursor: 'not-allowed', opacity: 0.6 }}
            >
              Add
            </ButtonText>
          </span>
        </Tooltip>
      </Flex>

      {deleteTableModal && (
        <DeleteFigureModal
          onClose={() => setDeleteTableModal(false)}
          isOpen={deleteTableModal}
          onDelete={() => handleDeleteSingleTable(selectedTableIndex)}
          selectedFigures={{ [selectedTableIndex]: true }}
          filedName="Table"
        />
      )}
    </Flex>
  );
};

export default FormFieldTables;
